import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Roles } from '@shared/util-auth/models/roles';
import { UserStore } from '@shared/data';
import { catchError, filter, map, Observable, of, take, timeout } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * A general Angular Guard which checks whether the user is authenticated
 * and has the roles associated with the route - given in 'data.roles' property
 * of the route.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private isLoaded$: Observable<boolean>;
  private userStore = inject(UserStore);
  private isDzBankUser = this.userStore.isDzBankUser;
  private isCorporateUser = this.userStore.isCorporateMode;
  private isRefiVisibleForVr = this.userStore.isRefiVisibleForVr;
  private isEvaluationVisibleForFK = this.userStore.isEvaluationVisibleForFk;
  private hasEvaluationPermission = this.userStore.hasEvaluationPermission;

  private router = inject(Router);

  constructor() {
    this.isLoaded$ = toObservable(this.userStore.loaded);
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const currentUrl = state.url;
    return this.isLoaded$.pipe(
      filter((isLoaded) => isLoaded),
      take(1),
      timeout(30000),
      map((permissionsLoaded) => {
        if (permissionsLoaded) {
          let routeUrl = currentUrl;
          if (currentUrl.indexOf('?') > 0) {
            routeUrl = routeUrl.substring(0, currentUrl.indexOf('?'));
          }

          return this.isRouteAllowed(routeUrl, next, state);
        }
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/']);
        return of(false);
      })
    );
  }

  private isRouteAllowed(url: string, next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    if (url === '/') {
      return true;
    }

    // 4. Benutzer IST authentifiziert. Führe die bisherige Berechtigungsprüfung durch.
    // Spezielle Prüfung für die 'refi'-Route
    if (state.url.startsWith('/refi') && !this.isDzBankUser()) {
      if (!this.isRefiVisibleForVr()) {
        return this.router.createUrlTree(['/']);
      }
    } else if (state.url.startsWith('/evaluation') && !this.isDzBankUser() && (!this.hasEvaluationPermission() || this.isCorporateUser())) {
      if (!this.isEvaluationVisibleForFK()) {
        return this.router.createUrlTree(['/']);
      }
    }

    if (next.data['roles']) {
      const requiredRoles: string[] = next.data['roles'];
      if (requiredRoles.includes(Roles.PERMIT_ALL)) {
        return true;
      }
      const hasRole = requiredRoles.some((role) => this.userStore.roles().includes(role));
      if (!hasRole) {
        console.warn(`AuthGuard: User does not have required role. Access denied to ${state.url}`);
        return this.router.createUrlTree(['/error']); // Oder eine "Unauthorized"-Seite
      }
      return true;
    }

    if (next.data['entitlements']) {
      const requiredEntitlements: string[] = next.data['entitlements'];
      const hasEntitlement = requiredEntitlements.some((entitlement) => this.userStore.entitlements().includes(entitlement));
      if (!hasEntitlement) {
        console.warn(`AuthGuard: User does not have required entitlement. Access denied to ${state.url}`);
        return this.router.createUrlTree(['/error']); // Oder eine "Unauthorized"-Seite
      }
      return true;
    }

    // Fallback, falls weder Rollen noch Berechtigungen für eine Route definiert sind.
    console.warn(`AuthGuard: No roles or entitlements defined for route ${state.url}. Access denied.`);
    return this.router.createUrlTree(['/error']);
  }
}
